// src/admin.ts
import { apiClient } from './client';
import type {
    ApiResponse,
    User,
    Product,
    Order,
    PaginatedResponse,
    DashboardStats,
    VerificationResponse,
    AddressVerification,
    IdentityVerification
} from '../types';

export const adminAPI = {
    // Users Management
    async getUsers(params?: {
        page?: number;
        page_size?: number;
        search?: string;
        role?: string;
        is_active?: boolean;
    }): Promise<ApiResponse<PaginatedResponse<User[]>>> {
        try {
            const response = await apiClient.get('/admin/users/', { params });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch users',
                status: error.response?.status || 500
            };
        }
    },

    async assignRole(userId: number, role: string): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/assign_role/`, { role });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.error || 'Failed to assign role',
                status: error.response?.status || 500
            };
        }
    },

    async removeRole(userId: number, role: string): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/remove_role/`, { role });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.error || 'Failed to remove role',
                status: error.response?.status || 500
            };
        }
    },

    async toggleUserStatus(userId: number): Promise<ApiResponse<{ is_active: boolean }>> {
        try {
            const response = await apiClient.patch(`/admin/users/${userId}/toggle_status/`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to toggle user status',
                status: error.response?.status || 500
            };
        }
    },

    // Products Management
    async getProducts(params?: {
        page?: number;
        page_size?: number;
        search?: string;
        status?: string;
        category?: string;
        show_inactive?: boolean;
    }): Promise<ApiResponse<PaginatedResponse<Product[]>>> {
        try {
            const response = await apiClient.get('/admin/products/', { params });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch products',
                status: error.response?.status || 500
            };
        }
    },

    async createProduct(productData: any): Promise<ApiResponse<Product>> {
        try {
            const response = await apiClient.post('admin/products/', productData);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            console.error('❌ Create failed:', error.response?.data);
            return {
                error: error.response?.data?.detail || 'Failed to create product',
                status: error.response?.status || 500
            };
        }
    },

    // This update uses the Django default, does not override to log the action.
    // async updateProduct(id: number, productData: Partial<Product>): Promise<ApiResponse<Product>> {
    //     try {
    //         const response = await apiClient.put(`/admin/products/${id}/`, productData);
    //         return { data: response.data, status: response.status };
    //     } catch (error: any) {
    //         return {
    //             error: error.response?.data?.detail || 'Failed to update product',
    //             status: error.response?.status || 500
    //         };
    //     }
    // },

    async deleteProduct(id: number): Promise<ApiResponse<void>> {
        try {
            const response = await apiClient.delete(`/admin/products/${id}/`);
            return { data: undefined, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to delete product',
                status: error.response?.status || 500
            };
        }
    },

    async restoreProduct(id: number): Promise<ApiResponse<Product>> {
        try {
            const response = await apiClient.post(`/admin/products/${id}/restore/`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to restore product',
                status: error.response?.status || 500
            };
        }
    },

    async getLowStockProducts(threshold: number = 5): Promise<ApiResponse<Product[]>> {
        try {
            const response = await apiClient.get(`/admin/products/low_stock/?threshold=${threshold}`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch low stock products',
                status: error.response?.status || 500
            };
        }
    },

    // Orders Management
    async getOrders(params?: {
        page?: number;
        page_size?: number;
        status?: string;
        search?: string;
        date_from?: string;
        date_to?: string;
    }): Promise<ApiResponse<PaginatedResponse<Order[]>>> {
        try {
            const response = await apiClient.get('/admin/orders/', { params });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch orders',
                status: error.response?.status || 500
            };
        }
    },

    async getOrder(id: number): Promise<ApiResponse<Order>> {
        try {
            const response = await apiClient.get(`/admin/orders/${id}/`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch order',
                status: error.response?.status || 500
            };
        }
    },

    async updateOrderStatus(id: number, status: string): Promise<ApiResponse<Order>> {
        try {
            const response = await apiClient.patch(`/admin/orders/${id}/update_status/`, { status });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to update order status',
                status: error.response?.status || 500
            };
        }
    },

    // Dashboard Stats
    async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
        try {
            const response = await apiClient.get('/admin/stats/');
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch dashboard stats',
                status: error.response?.status || 500
            };
        }
    },

    async suggestTags(data: {
        title: string;
        description?: string;
        specs?: Record<string, any>;
        type: 'categories' | 'keywords';
        existing_tags: string[];
    }): Promise<ApiResponse<{ suggestions: string[] }>> {
        try {
            const response = await apiClient.post('/admin/products/suggest-tags/', data);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.error || 'Failed to get suggestions',
                status: error.response?.status || 500
            };
        }
    },

    async getCommonCategories(): Promise<ApiResponse<{ categories: string[] }>> {
        try {
            const response = await apiClient.get('/admin/products/common-categories/');
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.error || 'Failed to fetch common categories',
                status: error.response?.status || 500
            };
        }
    },

    async getProduct(id: number): Promise<ApiResponse<Product>> {
        try {
            const response = await apiClient.get(`/admin/products/${id}/`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch product',
                status: error.response?.status || 500
            };
        }
    },

    async updateProductImages(productId: number, images: any[]): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post(`admin/products/${productId}/images/`, { images });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to update images',
                status: error.response?.status || 500
            };
        }
    },

    async getPendingVerifications(): Promise<ApiResponse<VerificationResponse>> {
        return apiClient.get('auth/users/verifications/pending/');
    },

    async getVerificationsByStatus(
        type: 'identity' | 'address',
        status: 'approved' | 'rejected',
        page: number = 1,
        pageSize: number = 20
    ): Promise<ApiResponse<any>> {
        const endpoint = type === 'identity'
            ? `/auth/users/verifications/${status}/`
            : `/auth/users/address-verifications/${status}/`;

        return apiClient.get(endpoint, {
            params: { page, page_size: pageSize }
        });
    },

    async searchVerifications(
        query: string,
        type: 'identity' | 'address' | 'all' = 'all',
        page: number = 1,
        pageSize: number = 20
    ): Promise<ApiResponse<{
        identity: IdentityVerification[];
        address: AddressVerification[];
    }>> {
        try {
            const response = await apiClient.get('/auth/users/verifications/search/', {
                params: { q: query, type, page, page_size: pageSize }
            });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to search verifications',
                status: error.response?.status || 500
            };
        }
    },

    async verifyIdentity(userId: number, approved: boolean, reason?: string): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post(`/auth/users/${userId}/verify-identity/`, {
                approved,
                reason: reason || null
            });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to verify identity',
                status: error.response?.status || 500
            };
        }
    },

    async getIdentityVerifications(
        status: 'pending' | 'approved' | 'rejected',
        page: number = 1,
        pageSize: number = 20
    ): Promise<ApiResponse<PaginatedResponse<IdentityVerification>>> {
        try {
            const response = await apiClient.get(`/auth/users/verifications/${status}/`, {
                params: { page, page_size: pageSize }
            });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || `Failed to fetch ${status} verifications`,
                status: error.response?.status || 500
            };
        }
    },

    async getAddressVerifications(
        status: 'pending' | 'approved' | 'rejected' | 'needs_location',
        page: number = 1,
        pageSize: number = 20
    ): Promise<ApiResponse<PaginatedResponse<AddressVerification>>> {
        try {
            const response = await apiClient.get(`/auth/users/address-verifications/${status}/`, {
                params: { page, page_size: pageSize }
            });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || `Failed to fetch ${status} address verifications`,
                status: error.response?.status || 500
            };
        }
    },

    // Vendor endpoints
    getVendors: async () => {
        try {
            const response = await apiClient.get('/management/admin/vendors/');
            return { data: response.data, error: null };
        } catch (error: any) {
            console.error('Failed to fetch vendors:', error);
            return {
                data: null,
                error: error.response?.data?.detail || error.response?.data?.message || 'Failed to load vendors'
            };
        }
    },

    getVendor: async (id: string) => {
        try {
            const response = await apiClient.get(`/management/admin/vendors/${id}/`);
            return { data: response.data, error: null };
        } catch (error: any) {
            console.error(`Failed to fetch vendor ${id}:`, error);
            return {
                data: null,
                error: error.response?.data?.detail || error.response?.data?.message || 'Failed to load vendor details'
            };
        }
    },

    getVendorProducts: async (vendorId: string, params?: {
        page?: number;
        page_size?: number;
        status?: string;
    }) => {
        try {
            const response = await apiClient.get(`/management/vendors/${vendorId}/products/`, { params }); //this should be moved to vendors.views.py for vendor use and a separate call for admin use
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to load products'
            };
        }
    },

    // Get all products (admin view)
    getAllProducts: async (params?: {
        status?: string;
        vendor?: string;
        search?: string;
        page?: number;
        page_size?: number;
    }) => {
        try {
            const response = await apiClient.get('/management/admin/products/all/', { params });
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to fetch products'
            };
        }
    },

    getVendorOrders: async (vendorId: string) => {
        try {
            const response = await apiClient.get(`/management/admin/vendors/${vendorId}/orders/`);
            return { data: response.data, error: null };
        } catch (error: any) {
            console.error(`Failed to fetch orders for vendor ${vendorId}:`, error);
            return {
                data: null,
                error: error.response?.data?.detail || error.response?.data?.message || 'Failed to load orders'
            };
        }
    },

    updateVendor: async (id: string, data: any) => {
        try {
            const response = await apiClient.patch(`/management/admin/vendors/${id}/`, data);
            return { data: response.data, error: null };
        } catch (error: any) {
            console.error(`Failed to update vendor ${id}:`, error);
            return {
                data: null,
                error: error.response?.data?.detail || error.response?.data?.message || 'Failed to update vendor'
            };
        }
    },
    // Product version review
    reviewVersion: async (params: {
        versionId?: number;
        productId?: number;
        action: 'approve' | 'reject' | 'rollback';
        reason?: string;
    }) => {
        try {
            const response = await apiClient.post('/management/admin/products/review-version/', params);
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to review'
            };
        }
    },

    // Get pending version for a product
    getPendingVersion: async (productId: number) => {
        try {
            const response = await apiClient.get(`/management/admin/products/${productId}/pending-version/`);
            return { data: response.data, error: null };
        } catch (error: any) {
            // Don't treat 404 as an error - just means no pending version
            if (error.response?.status === 404) {
                return { data: null, error: null };
            }
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to fetch pending version'
            };
        }
    },

    // Get product versions (for history view)
    getProductVersions: async (productId: number) => {
        try {
            const response = await apiClient.get(`/management/admin/products/${productId}/versions/`);
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to fetch product versions'
            };
        }
    },

    // Get a specific product version
    getVersion: async (versionId: number) => {
        try {
            const response = await apiClient.get(`/management/admin/products/versions/${versionId}/`);
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to fetch version details'
            };
        }
    },

    // Toggle product status
    adminToggleProductActive: (id: number, payload: { is_active: boolean; reason?: string }) => {
        return apiClient.patch(`/admin/products/${id}/toggle-active/`, payload);
    },

    adminToggleVariantActive: (productId: number, variantId: number, payload: { is_active: boolean; reason?: string }) => {
        return apiClient.patch(`/admin/products/${productId}/variants/${variantId}/toggle-active/`, payload);
    },

    getVendorApplications: async (params?: any) => {
        const response = await apiClient.get('/admin/vendor-applications/applications/', { params });
        return response.data;
    },

    getVendorApplication: async (id: number) => {
        const response = await apiClient.get(`/admin/vendor-applications/${id}/`);
        return response.data;
    },

    approveVendorApplication: async (id: number) => {
        const response = await apiClient.post(`/admin/vendor-applications/${id}/approve/`);
        return response.data;
    },

    rejectVendorApplication: async (id: number, reason: string) => {
        const response = await apiClient.post(`/admin/vendor-applications/${id}/reject/`, { reason });
        return response.data;
    },

    getVendorTypeChangeRequests: async (params?: { status?: string; page?: number; page_size?: number }) => {
        const response = await apiClient.get('/admin/vendor-type-change-requests/', { params });
        return response.data;
    },

    getVendorTypeChangeRequest: async (id: number) => {
        const response = await apiClient.get(`/admin/vendor-type-change-requests/${id}/`);
        return response.data;
    },

    reviewVendorTypeChange: async (id: number, data: { action: 'approve' | 'reject'; reason?: string }) => {
        const response = await apiClient.post(`/admin/vendor-type-change-requests/${id}/review/`, data);
        return response.data;
    },
};